// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, BaseUploadImage, Loading } from '~/components/common';
// Others
import { defaultValuesCategoryForm, schema } from './helper';
import { IBaseFile, IBodyUpdate } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IFormCategory, IFormUpdateCategory } from '~/utils/interface/category';
import { compareDataUpdate, createFormData, removeEmptyObjects } from '~/utils/helper/common';
import { createCategory, getDetailCategory, updateCategory } from '~/thunks/category/categoryThunk';
// Styles, images, icons
import styles from './CategoryForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idCategory?: string;
  onClose: () => void;
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const CategoryFormModal = (props: Props) => {
  //#region Destructuring Props
  const { idCategory, onClose, onRemoveFile } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/category', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormCategory>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesCategoryForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { isLoading } = useAppSelector((state) => state.category);
  //#endregion Selector

  //#region Declare State
  const [initialData, setInitialData] = useState<IFormCategory>(defaultValuesCategoryForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idCategory) return;

    handleGetDetailCategory(idCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCategory]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleFormCategory = (data: IFormCategory) => {
    if (idCategory) {
      handleEditCategory(data);
    } else {
      handleAddCategory(data);
    }
  };

  const handleAddCategory = async (data: IFormCategory) => {
    const formData = await createFormData(removeEmptyObjects(data), ['categoryImage']);

    dispatch(createCategory(formData))
      .unwrap()
      .then((_res) => {
        handleCloseModal();
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleEditCategory = async (data: IFormUpdateCategory) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormUpdateCategory;

    const formData = await createFormData(removeEmptyObjects(dataChange), ['categoryImage']);

    const params: IBodyUpdate<FormData> = {
      id: idCategory || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateCategory(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {});
  };

  const handleGetDetailCategory = (idCategory: string) => {
    if (!idCategory) return;

    dispatch(getDetailCategory(idCategory))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, categoryImage, ...remainingData } = res?.data;

        const body: IFormCategory = {
          ...remainingData,
          description: remainingData.description || EMPTY_STRING,
          categoryImage: categoryImage ? { url: categoryImage } : {},
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleRemoveFile = (file: IBaseFile) => {
    if (!file.id) return;

    onRemoveFile?.(file.id);
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='CategoryFormModalComponent'
      aria-labelledby='CategoryFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='trailerFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(idCategory ? 'form_add_category_title_edit' : 'form_add_category_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_category_name_title')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'description'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_category_description_title')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.description?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'categoryImage'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseUploadImage
                    label={t('form_add_category_upload_image_title')}
                    {...(idCategory && !value?.file && { uploadedFiles: [value || {}] })}
                    isMultiple={false}
                    isButton={false}
                    onChange={(files: IBaseFile[]) => onChange(files?.[0])}
                    onRemove={handleRemoveFile}
                    height={130}
                    errorMessage={errors.categoryImage?.message}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormCategory)}
          disabled={idCategory ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default CategoryFormModal;
